<template>
  <div class="feedback-widget" :class="{ 'feedback-widget--with-price-toggle': showPriceToggle }">
    <button
      v-if="!open"
      class="feedback-trigger"
      type="button"
      aria-haspopup="dialog"
      @click="openForm"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 4h16v12H8l-4 4V4Zm4 5h8M8 12h5" />
      </svg>
      Feedback
    </button>

    <div
      v-if="open"
      class="feedback-overlay"
      role="presentation"
      @click.self="closeForm"
    >
      <section
        class="feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
      >
        <header class="feedback-header">
          <div>
            <span class="feedback-kicker">Help improve GixCore</span>
            <h2 id="feedback-title">Send feedback</h2>
          </div>
          <button class="feedback-close" type="button" aria-label="Close feedback form" @click="closeForm">
            &times;
          </button>
        </header>

        <div v-if="sent" class="feedback-success" role="status">
          <span class="feedback-success-icon" aria-hidden="true">&#10003;</span>
          <strong>Thanks for the feedback.</strong>
          <p>Your note was sent to the GixCore team.</p>
          <button type="button" @click="closeForm">Done</button>
        </div>

        <form v-else class="feedback-form" @submit.prevent="submitFeedback">
          <div class="feedback-row">
            <label>
              <span>Type</span>
              <select v-model="kind">
                <option value="feedback">General feedback</option>
                <option value="idea">Idea</option>
                <option value="bug">Bug report</option>
              </select>
            </label>

            <label>
              <span>Email <small>optional</small></span>
              <input
                v-model.trim="email"
                type="email"
                autocomplete="email"
                maxlength="254"
                placeholder="you@example.com"
              >
            </label>
          </div>

          <label class="feedback-message">
            <span>Message</span>
            <textarea
              ref="message"
              v-model="message"
              required
              minlength="10"
              maxlength="2000"
              rows="5"
              placeholder="What worked, what did not, or what would make the builder better?"
            ></textarea>
            <small>{{ message.length }}/2000</small>
          </label>

          <label class="feedback-honeypot" aria-hidden="true">
            Website
            <input v-model="website" type="text" tabindex="-1" autocomplete="off">
          </label>

          <TurnstileWidget
            ref="captcha"
            action="feedback"
            @verified="captchaToken = $event"
            @expired="captchaToken = ''"
            @error="onCaptchaError"
          />

          <p v-if="errorMessage" class="feedback-error" role="alert">{{ errorMessage }}</p>

          <footer class="feedback-actions">
            <button class="feedback-cancel" type="button" :disabled="submitting" @click="closeForm">
              Cancel
            </button>
            <button class="feedback-submit" type="submit" :disabled="submitDisabled">
              {{ submitting ? 'Sending...' : 'Send feedback' }}
            </button>
          </footer>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import TurnstileWidget from '../admin/TurnstileWidget.vue'
import { apiDomain, SHOW_PRICE_TOGGLE } from '../../config'

export default {
  name: 'FeedbackWidget',
  components: {
    TurnstileWidget
  },
  data () {
    return {
      open: false,
      sent: false,
      submitting: false,
      kind: 'feedback',
      email: '',
      message: '',
      website: '',
      captchaToken: '',
      errorMessage: '',
      showPriceToggle: SHOW_PRICE_TOGGLE
    }
  },
  computed: {
    submitDisabled () {
      return this.submitting || this.message.trim().length < 10 || !this.captchaToken
    }
  },
  mounted () {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    openForm () {
      this.open = true
      this.sent = false
      this.errorMessage = ''
      this.$nextTick(() => {
        if (this.$refs.message) this.$refs.message.focus()
      })
    },
    closeForm () {
      if (this.submitting) return
      this.open = false
    },
    onKeydown (event) {
      if (event.key === 'Escape' && this.open) this.closeForm()
    },
    onCaptchaError () {
      this.captchaToken = ''
      this.errorMessage = 'The security check could not be completed. Please try again.'
    },
    resetCaptcha () {
      this.captchaToken = ''
      if (this.$refs.captcha && this.$refs.captcha.reset) this.$refs.captcha.reset()
    },
    async submitFeedback () {
      if (this.submitDisabled) return

      this.submitting = true
      this.errorMessage = ''

      try {
        await axios.post(`${apiDomain}feedback`, {
          kind: this.kind,
          email: this.email || null,
          message: this.message.trim(),
          website: this.website,
          captchaToken: this.captchaToken,
          pageUrl: window.location.href,
          route: this.$route.fullPath,
          userAgent: window.navigator.userAgent
        })

        this.sent = true
        this.message = ''
        this.website = ''
        this.captchaToken = ''
      } catch (error) {
        const responseMessage = error
          && error.response
          && error.response.data
          && error.response.data.message
        this.errorMessage = responseMessage || 'Feedback could not be sent. Please try again.'
        this.resetCaptcha()
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.feedback-widget {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 12000;
  width: 112px;
  height: 36px;
}

.feedback-widget--with-price-toggle {
  right: 200px;
}

.feedback-trigger {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid rgba(25, 151, 198, 0.62);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.94);
  color: #cfd7e5;
  font-size: 12px;
  font-weight: 750;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  cursor: pointer;
}

.feedback-trigger:hover {
  border-color: #22b9ef;
  color: #fff;
  transform: translateY(-1px);
}

.feedback-trigger svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: #22b9ef;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.feedback-overlay {
  position: fixed;
  inset: 0;
  z-index: 12000;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(3, 5, 9, 0.76);
  backdrop-filter: blur(5px);
}

.feedback-dialog {
  width: min(480px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 10px;
  background:
    radial-gradient(circle at 80% 0%, rgba(25, 151, 198, 0.12), transparent 38%),
    #0d1118;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.62);
  color: #d4d9e3;
}

.feedback-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.7);
}

.feedback-kicker,
.feedback-form label > span {
  color: #7e899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.feedback-header h2 {
  margin: 4px 0 0;
  color: #edf2fa;
  font-size: 21px;
  font-weight: 700;
}

.feedback-close {
  padding: 0;
  border: 0;
  background: transparent;
  color: #8c96a7;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.feedback-form {
  display: grid;
  gap: 15px;
  padding: 20px 22px 22px;
}

.feedback-row {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 12px;
}

.feedback-form label {
  display: grid;
  gap: 7px;
  margin: 0;
}

.feedback-form label small {
  color: #5f6878;
  font-size: 9px;
  letter-spacing: 0;
  text-transform: none;
}

.feedback-form input,
.feedback-form select,
.feedback-form textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 7px;
  outline: none;
  background: rgba(6, 9, 14, 0.86);
  color: #dce2ec;
  font: inherit;
  font-size: 13px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.feedback-form input,
.feedback-form select {
  height: 38px;
  padding: 0 10px;
}

.feedback-form textarea {
  min-height: 118px;
  padding: 10px;
  line-height: 1.48;
  resize: vertical;
}

.feedback-form input:focus,
.feedback-form select:focus,
.feedback-form textarea:focus {
  border-color: rgba(25, 151, 198, 0.9);
  box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.1);
}

.feedback-message {
  position: relative;
}

.feedback-message > small {
  justify-self: end;
  margin-top: -3px;
  color: #687284;
  font-size: 10px;
}

.feedback-honeypot {
  position: absolute !important;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.feedback-error {
  margin: 0;
  padding: 9px 11px;
  border: 1px solid rgba(230, 71, 89, 0.42);
  border-radius: 6px;
  background: rgba(230, 71, 89, 0.08);
  color: #f08a95;
  font-size: 12px;
  line-height: 1.4;
}

.feedback-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 2px;
}

.feedback-actions button,
.feedback-success button {
  min-height: 37px;
  padding: 0 14px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
}

.feedback-cancel {
  border: 1px solid rgba(67, 72, 87, 0.9);
  background: transparent;
  color: #9aa4b5;
}

.feedback-submit,
.feedback-success button {
  border: 1px solid #1997c6;
  background: #1997c6;
  color: #061018;
}

.feedback-submit:disabled,
.feedback-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.feedback-success {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 34px 24px 30px;
  text-align: center;
}

.feedback-success-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  margin-bottom: 4px;
  border: 1px solid rgba(0, 188, 140, 0.55);
  border-radius: 50%;
  background: rgba(0, 188, 140, 0.1);
  color: #70ddbd;
  font-size: 20px;
}

.feedback-success strong {
  color: #eef4fb;
  font-size: 17px;
}

.feedback-success p {
  margin: 0 0 10px;
  color: #8e98a9;
  font-size: 13px;
}

@media (max-width: 620px) {
  .feedback-widget {
    right: 16px;
    bottom: 16px;
  }

  .feedback-widget--with-price-toggle {
    right: 200px;
  }

  .feedback-trigger {
    right: 0;
    bottom: 0;
  }

  .feedback-overlay {
    align-items: end;
    padding: 10px;
  }

  .feedback-dialog {
    max-height: calc(100vh - 20px);
  }

  .feedback-row {
    grid-template-columns: 1fr;
  }
}
</style>
